import { useState, useEffect, useCallback, Fragment } from "react";
import { colors } from "../theme";
import { IdleGroveLogo } from "../components/IdleGroveLogo";
import { ConcordiumLogo } from "../components/ConcordiumLogo";

interface SlideProps {
  isDark: boolean;
}

function Slide({
  isDark,
  children,
}: SlideProps & { children: React.ReactNode }) {
  return (
    <div
      className="slide w-[1920px] h-[1080px] p-16 flex flex-col relative overflow-hidden"
      style={{
        backgroundColor: isDark ? colors.base03 : colors.base3,
        color: isDark ? colors.base0 : colors.base00,
        fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
        pageBreakAfter: "always",
        pageBreakInside: "avoid",
      }}
    >
      {children}
    </div>
  );
}

function SlideHeader({
  isDark,
  subtitle,
}: {
  isDark: boolean;
  subtitle: string;
}) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  return (
    <div className="flex items-center justify-between mb-10">
      <div className="flex items-center gap-4">
        <IdleGroveLogo size={72} isDark={isDark} />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Idle Grove</h1>
          <p
            className="text-lg"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            {subtitle}
          </p>
        </div>
      </div>
      <div
        className="px-5 py-2.5 rounded-full text-base font-medium"
        style={{
          backgroundColor: `${accentColor}20`,
          color: accentColor,
        }}
      >
        idlegrove.com
      </div>
    </div>
  );
}

function SlideFooter({
  isDark,
  left,
  right,
}: {
  isDark: boolean;
  left: string;
  right: string;
}) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  return (
    <div
      className="mt-auto pt-6 flex items-center justify-between"
      style={{ borderColor: isDark ? colors.base02 : colors.base2 }}
    >
      <p style={{ color: isDark ? colors.base01 : colors.base1 }}>{left}</p>
      <p className="font-medium" style={{ color: accentColor }}>
        {right}
      </p>
    </div>
  );
}

function PricingChart({ isDark }: { isDark: boolean }) {
  const accentColor = isDark ? colors.cyan : colors.blue;
  const tradColor = isDark ? colors.base01 : colors.base1;

  return (
    <div className="flex-1 flex items-end gap-3 pb-4">
      {[100, 82, 65, 50, 38, 28].map((height, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-2">
          <div className="w-full flex gap-1.5" style={{ height: "180px" }}>
            <div
              className="flex-1 rounded-t-lg"
              style={{
                height: `${height}%`,
                backgroundColor: accentColor,
                marginTop: "auto",
              }}
            />
            <div
              className="flex-1 rounded-t-lg"
              style={{
                height: "100%",
                backgroundColor: tradColor,
                opacity: 0.3,
              }}
            />
          </div>
          <span
            className="text-sm font-medium"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            Y{i + 1}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatBlock({
  value,
  label,
  isDark,
  color,
}: {
  value: string;
  label: string;
  isDark: boolean;
  color?: string;
}) {
  const accentColor = color || (isDark ? colors.cyan : colors.blue);
  return (
    <div className="text-center">
      <div className="text-4xl font-bold mb-1" style={{ color: accentColor }}>
        {value}
      </div>
      <div
        className="text-base font-medium"
        style={{ color: isDark ? colors.base01 : colors.base1 }}
      >
        {label}
      </div>
    </div>
  );
}

// =============================================================================
// VISUAL COMPONENTS
// =============================================================================

function NodeGrid({
  rows = 3,
  cols = 24,
  activeIndices = [7, 18, 35, 42, 53, 66],
  isDark = true,
}: {
  rows?: number;
  cols?: number;
  activeIndices?: number[];
  isDark?: boolean;
}) {
  const activeColor = isDark ? colors.cyan : colors.blue;
  const inactiveColor = isDark ? colors.base02 : colors.base2;

  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {Array.from({ length: rows * cols }, (_, i) => {
        const isActive = activeIndices.includes(i);
        return (
          <div
            key={i}
            className="w-6 h-6 rounded"
            style={{
              backgroundColor: isActive ? activeColor : inactiveColor,
              boxShadow: isActive ? `0 0 16px ${activeColor}50` : "none",
            }}
          />
        );
      })}
    </div>
  );
}

function DataFlowDiagram() {
  const steps = [
    { label: "Ingest", icon: "↓", color: colors.blue },
    { label: "Index & Classify", icon: "◎", color: colors.magenta, sub: "ML" },
    { label: "Store", icon: "⏻", color: colors.cyan, sub: "Power Off" },
    { label: "Search", icon: "◐", color: colors.violet, sub: "Always On" },
    {
      label: "Retrieve",
      icon: "↗",
      color: colors.green,
      sub: "Wake on Demand",
    },
  ];

  return (
    <div className="flex items-start justify-center gap-5">
      {steps.map((step, i) => (
        <Fragment key={step.label}>
          <div className="flex flex-col items-center">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-lg mb-2"
              style={{ backgroundColor: `${step.color}25`, color: step.color }}
            >
              {step.icon}
            </div>
            <span className="text-xs font-semibold whitespace-nowrap">
              {step.label}
            </span>
            {step.sub && (
              <span
                className="text-xs whitespace-nowrap"
                style={{ color: colors.base01 }}
              >
                {step.sub}
              </span>
            )}
          </div>
          {i < steps.length - 1 && (
            <span
              className="text-3xl font-bold mt-2"
              style={{ color: colors.base01 }}
            >
              →
            </span>
          )}
        </Fragment>
      ))}
    </div>
  );
}

// =============================================================================
// SLIDE 1: BUSINESS CASE (Light Mode)
// =============================================================================
function BusinessSlide() {
  const isDark = false;
  const accentColor = colors.blue;

  const verticals = [
    { name: "Healthcare", desc: "HIPAA-compliant long-term records" },
    { name: "Media & Entertainment", desc: "Raw footage and master archives" },
    { name: "Legal & Finance", desc: "Compliance and audit trails" },
    {
      name: "Research & Government",
      desc: "Scientific data and public records",
    },
    { name: "Gaming & Esports", desc: "Build archives, player data, and VODs" },
    { name: "MSPs & Hosting", desc: "White-label archive tiers for clients" },
    { name: "Indigenous Orgs", desc: "Sovereign data, community archives" },
  ];

  const valueProps = [
    {
      icon: "↓",
      title: "Costs decrease over time",
      desc: "Old data shouldn't cost the same as yesterday's",
    },
    {
      icon: "↗",
      title: "No egress surprises",
      desc: "Predictable pricing when you need data back",
    },
    {
      icon: "◎",
      title: "Intelligent by default",
      desc: "Indexed, classified, and searchable from day one",
    },
    {
      icon: "♻",
      title: "Green energy ready",
      desc: "Off-grid capable; on-prem hardware can connect to your renewables",
    },
    {
      icon: "⬡",
      title: "Lock in your rates",
      desc: "Long-term contracts available to protect against price volatility",
    },
  ];

  return (
    <Slide isDark={isDark}>
      {/* Subtle node grid background in header area */}
      <div className="absolute -top-4 left-0 right-0 h-48 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-end pr-8">
          <NodeGrid
            rows={3}
            cols={36}
            activeIndices={[5, 12, 19, 31, 38, 52, 58, 67]}
            isDark={false}
          />
        </div>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, ${colors.base3} 100%)`,
          }}
        />
      </div>

      <SlideHeader
        isDark={isDark}
        subtitle="Ultra-efficient cold storage infrastructure"
      />

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column */}
        <div className="flex flex-col">
          <h2 className="text-5xl font-bold leading-tight mb-4">
            Archive storage that costs less the longer you keep it
          </h2>
          <p
            className="text-xl mb-8 leading-relaxed"
            style={{ color: colors.base1 }}
          >
            Most data just sits there. Backups, compliance records, old
            projects—rarely accessed but can't be deleted. Traditional cloud
            charges the same price forever. We think that's backwards.
          </p>

          {/* Value props */}
          <div className="space-y-4 mb-8">
            {valueProps.map((v, i) => (
              <div key={i} className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                  style={{
                    backgroundColor: `${accentColor}15`,
                    color: accentColor,
                  }}
                >
                  {v.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{v.title}</h4>
                  <p className="text-base" style={{ color: colors.base1 }}>
                    {v.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div
            className="grid grid-cols-3 gap-6 p-6 rounded-2xl mt-auto"
            style={{ backgroundColor: colors.base2 }}
          >
            <StatBlock value="75%" label="Energy Savings" isDark={isDark} />
            <StatBlock value="10+ PB" label="Per Rack" isDark={isDark} />
            <StatBlock value="$0" label="Egress Traps" isDark={isDark} />
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col">
          {/* Pricing chart */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Pricing over time</h3>
              <div className="flex items-center gap-5 text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: accentColor }}
                  />
                  <span>Idle Grove</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded opacity-30"
                    style={{ backgroundColor: colors.base1 }}
                  />
                  <span style={{ color: colors.base1 }}>Traditional</span>
                </div>
              </div>
            </div>
            <PricingChart isDark={isDark} />
          </div>

          {/* Target verticals */}
          <div
            className="flex-1 rounded-2xl p-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              Built for long-term archives
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {verticals.map((v, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: colors.base3 }}
                >
                  <h4 className="font-semibold mb-1">{v.name}</h4>
                  <p className="text-sm" style={{ color: colors.base1 }}>
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="You save. We save. The planet saves."
        right="Learn more → idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 2: ARCHITECTURE & ROADMAP (Dark Mode)
// =============================================================================
function TechSlide() {
  const isDark = true;
  const accentColor = colors.cyan;

  const architectureFeatures = [
    {
      icon: "⏻",
      title: "True Power-Off",
      desc: "Drives electrically disconnected when idle. Not sleep mode—actually off.",
      color: colors.cyan,
    },
    {
      icon: "◐",
      title: "Smart Scheduling",
      desc: "Batches requests, learns access patterns, wakes only what's needed.",
      color: colors.blue,
    },
    {
      icon: "◎",
      title: "Semantic Search",
      desc: "Content indexed on ingest. Image recognition, audio transcription, auto-classification.",
      color: colors.magenta,
    },
    {
      icon: "◈",
      title: "Distributed Resilience",
      desc: "Erasure coding across drives. Hardware fails; your data doesn't.",
      color: colors.violet,
    },
  ];

  const roadmap = [
    {
      name: "Extra Durability",
      desc: "Enhanced redundancy with encrypted parity to partner sites",
    },
    { name: "Priority Access", desc: "Faster wake times on demand" },
    { name: "Private Inference", desc: "PII/HIPAA-compliant ML on your data" },
    {
      name: "Data Scrubs & Reports",
      desc: "Integrity verification and compliance reporting",
    },
  ];

  const specs = [
    { label: "Per Box", storage: "70 TB", power: "25 W" },
    { label: "Per Shelf", storage: "1.47 PB", power: "600 W" },
    { label: "Per Rack", storage: "10 PB", power: "4.5 kW" },
  ];

  return (
    <Slide isDark={isDark}>
      {/* Subtle node grid background in header area */}
      <div className="absolute -top-4 left-0 right-0 h-48 overflow-hidden opacity-40 pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-end pr-8">
          <NodeGrid
            rows={3}
            cols={36}
            activeIndices={[8, 15, 23, 34, 41, 55, 62, 68]}
            isDark={true}
          />
        </div>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, ${colors.base03} 100%)`,
          }}
        />
      </div>

      <SlideHeader isDark={isDark} subtitle="Architecture & Roadmap" />

      <div className="flex-1 grid grid-cols-5 gap-10">
        {/* Left column - Architecture (3 cols) */}
        <div className="col-span-3 flex flex-col">
          <h2 className="text-4xl font-bold leading-tight mb-2">
            Truly off means truly off
          </h2>
          <p className="text-lg mb-6" style={{ color: colors.base01 }}>
            Purpose-built infrastructure where dormant data lives on hardware
            that's physically powered down.
          </p>

          {/* Architecture grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {architectureFeatures.map((f, i) => (
              <div
                key={i}
                className="p-5 rounded-xl"
                style={{ backgroundColor: colors.base02 }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-xl shrink-0"
                    style={{ backgroundColor: `${f.color}20`, color: f.color }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">{f.title}</h4>
                    <p className="text-sm" style={{ color: colors.base01 }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Data lifecycle */}
          <div
            className="rounded-xl p-6 mb-4"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-4">Data Lifecycle</h3>
            <DataFlowDiagram />
          </div>

          {/* Specs table */}
          <div
            className="rounded-xl p-5"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-4">Hardware Specifications</h3>
            <div className="grid grid-cols-3 gap-4">
              {specs.map((s, i) => (
                <div key={i} className="text-center">
                  <div
                    className="text-sm mb-2"
                    style={{ color: colors.base01 }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: accentColor }}
                  >
                    {s.storage}
                  </div>
                  <div className="text-sm" style={{ color: colors.base01 }}>
                    @ {s.power} max
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column - Roadmap (2 cols) */}
        <div className="col-span-2 flex flex-col">
          <div
            className="rounded-xl p-6 flex-1"
            style={{ backgroundColor: colors.base02 }}
          >
            <div className="flex items-center gap-2 mb-5">
              <span
                className="text-xs uppercase tracking-widest px-2 py-1 rounded-full"
                style={{
                  backgroundColor: `${colors.magenta}20`,
                  color: colors.magenta,
                }}
              >
                Coming Soon
              </span>
              <h3 className="font-semibold text-lg">Feature Roadmap</h3>
            </div>

            <div className="space-y-4">
              {roadmap.map((r, i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.base03 }}
                >
                  <h4 className="font-semibold mb-1">{r.name}</h4>
                  <p className="text-sm" style={{ color: colors.base01 }}>
                    {r.desc}
                  </p>
                </div>
              ))}
            </div>

            <div
              className="mt-6 p-4 rounded-lg border-2 border-dashed"
              style={{ borderColor: colors.base01 }}
            >
              <h4 className="font-semibold mb-1">Two paths to market</h4>
              <p className="text-sm" style={{ color: colors.base01 }}>
                <strong style={{ color: colors.base0 }}>Cloud storage</strong> —
                S3-compatible archive service
              </p>
              <p className="text-sm" style={{ color: colors.base01 }}>
                <strong style={{ color: colors.base0 }}>On-prem / colo</strong>{" "}
                — Hardware-as-a-service for regulated data
              </p>
            </div>
          </div>

          {/* Validation */}
          <div
            className="rounded-xl p-5 mt-4"
            style={{ backgroundColor: colors.base02 }}
          >
            <h3 className="font-semibold mb-3">Validated Architecture</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Core SDS software on ARM</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Power budgets lab tested</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Initial custom hardware stable</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: colors.green }}>✓</span>
                <span>Flexible erasure coding working</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="The hard parts are validated. Now we're building toward production."
        right="Learn more → idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// SLIDE 3: AUDIT & COMPLIANCE (Light Mode) - Concordium Grant Pitch
// =============================================================================
function AuditSlide() {
  const isDark = false;
  const accentColor = colors.blue;

  const sealingFlow = [
    { label: "Edge Capture", icon: "◉", color: colors.blue, sub: "Every Node" },
    { label: "Bundle", icon: "▦", color: colors.violet, sub: "Logs + Metrics" },
    { label: "Hash", icon: "⬡", color: colors.cyan, sub: "Cryptographic" },
    { label: "Seal", icon: "◈", color: colors.magenta, sub: "Concordium L1" },
    { label: "Verify", icon: "✓", color: colors.green, sub: "Tamper-Proof" },
  ];

  const dataTypes = [
    {
      name: "Raw Access Logs",
      desc: "User ID, file ID, timestamp, action type",
    },
    { name: "Columnar Metrics", desc: "Aggregated access patterns per window" },
    { name: "Timeseries Data", desc: "Historical trends, usage analytics" },
  ];

  const benefits = [
    {
      icon: "🔒",
      title: "Immutable proof",
      desc: "L1 finality means audit trails can't be altered retroactively",
    },
    {
      icon: "⚡",
      title: "Verified alerts",
      desc: "Anomaly detection with cryptographic proof—not just logs, but evidence",
    },
    {
      icon: "🤝",
      title: "Third-party verifiable",
      desc: "Auditors verify seals without accessing underlying customer data",
    },
  ];

  return (
    <Slide isDark={isDark}>
      {/* Subtle node grid background */}
      <div className="absolute -top-4 left-0 right-0 h-48 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-end pr-8">
          <NodeGrid
            rows={3}
            cols={36}
            activeIndices={[3, 17, 28, 44, 51, 65, 71, 82]}
            isDark={false}
          />
        </div>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, ${colors.base3} 100%)`,
          }}
        />
      </div>

      {/* Custom header with both logos */}
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-6">
          <IdleGroveLogo size={72} isDark={isDark} />
          <span className="text-2xl font-light" style={{ color: colors.base1 }}>
            ×
          </span>
          <ConcordiumLogo height={32} isDark={isDark} />
        </div>
        <div
          className="px-5 py-2.5 rounded-full text-base font-medium"
          style={{
            backgroundColor: `${colors.blue}20`,
            color: colors.blue,
          }}
        >
          Integration Proposal
        </div>
      </div>

      <div className="flex-1 grid grid-cols-2 gap-14">
        {/* Left column */}
        <div className="flex flex-col">
          <h2 className="text-5xl font-bold leading-tight mb-4">
            Tamper-proof audit trails, sealed on-chain
          </h2>
          <p
            className="text-xl mb-8 leading-relaxed"
            style={{ color: colors.base1 }}
          >
            Every archive access generates verifiable proof. Edge nodes capture
            and hash logs locally, then seal a zero-knowledge proof to
            Concordium—audit integrity without emitting sensitive data to L1.
          </p>

          {/* Benefits */}
          <div className="space-y-4 mb-8">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                  style={{
                    backgroundColor: `${accentColor}15`,
                    color: accentColor,
                  }}
                >
                  {b.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{b.title}</h4>
                  <p className="text-base" style={{ color: colors.base1 }}>
                    {b.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Audit UI mockup */}
          <div className="mt-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Audit Log</h3>
              <div
                className="flex items-center gap-1.5 text-sm"
                style={{ color: colors.base1 }}
              >
                <span>Seal contains:</span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Access Logs
                </span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Metrics
                </span>
                <span
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.base2 }}
                >
                  Timeseries
                </span>
              </div>
            </div>
            <div
              className="rounded-xl overflow-hidden text-sm"
              style={{ border: `1px solid ${colors.base2}` }}
            >
              {/* Table header */}
              <div
                className="grid grid-cols-12 gap-2 px-3 py-2 font-semibold"
                style={{ backgroundColor: colors.base2 }}
              >
                <div className="col-span-2">Time</div>
                <div className="col-span-2">Edge</div>
                <div className="col-span-2">Window</div>
                <div className="col-span-1 text-right">Recs</div>
                <div className="col-span-1 text-right">Size</div>
                <div className="col-span-2">Seal</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              {/* Table rows */}
              {[
                {
                  time: "14:32:01",
                  edge: "edge-ca-w1",
                  window: "12:00-18:00",
                  records: "2,847",
                  size: "4.2 MB",
                  seal: "7f3a9e2b",
                  status: "sealed",
                },
                {
                  time: "14:31:58",
                  edge: "edge-us-w1",
                  window: "12:00-18:00",
                  records: "1,923",
                  size: "2.8 MB",
                  seal: "2b8c4e1f",
                  status: "sealed",
                },
                {
                  time: "14:31:42",
                  edge: "edge-eu-c1",
                  window: "12:00-18:00",
                  records: "3,104",
                  size: "5.1 MB",
                  seal: "9d1f6a3c",
                  status: "sealed",
                },
                {
                  time: "14:31:39",
                  edge: "edge-ap-s1",
                  window: "12:00-18:00",
                  records: "1,456",
                  size: "2.1 MB",
                  seal: "—",
                  status: "sealing",
                },
              ].map((row, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
                  style={{
                    backgroundColor:
                      i % 2 === 0 ? colors.base3 : `${colors.base2}50`,
                  }}
                >
                  <div className="col-span-2" style={{ color: colors.base1 }}>
                    {row.time}
                  </div>
                  <div className="col-span-2 font-medium">{row.edge}</div>
                  <div className="col-span-2" style={{ color: colors.base1 }}>
                    {row.window}
                  </div>
                  <div
                    className="col-span-1 text-right"
                    style={{ color: colors.base1 }}
                  >
                    {row.records}
                  </div>
                  <div
                    className="col-span-1 text-right"
                    style={{ color: colors.base1 }}
                  >
                    {row.size}
                  </div>
                  <code className="col-span-2" style={{ color: colors.base1 }}>
                    {row.seal}...
                  </code>
                  <div className="col-span-2 flex justify-end">
                    {row.status === "sealed" ? (
                      <span
                        className="px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${colors.green}20`,
                          color: colors.green,
                        }}
                      >
                        ✓ Sealed
                      </span>
                    ) : (
                      <span
                        className="px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${colors.yellow}20`,
                          color: colors.yellow,
                        }}
                      >
                        ◌ Sealing
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Future vision */}
          <div className="flex items-center gap-3 mt-8">
            <div
              className="text-xs px-2.5 py-1 rounded-full shrink-0"
              style={{
                backgroundColor: `${colors.violet}15`,
                color: colors.violet,
              }}
            >
              Future
            </div>
            <p className="text-sm" style={{ color: colors.base1 }}>
              Archival stablecoin for prepaid storage, opening web3 capacity
              markets.
            </p>
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col">
          {/* Sealing flow */}
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-6">Sealing Flow</h3>
            <div className="flex items-start justify-between">
              {sealingFlow.map((step, i) => (
                <Fragment key={step.label}>
                  <div className="flex flex-col items-center">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-xl mb-2"
                      style={{
                        backgroundColor: `${step.color}20`,
                        color: step.color,
                      }}
                    >
                      {step.icon}
                    </div>
                    <span className="text-sm font-semibold whitespace-nowrap">
                      {step.label}
                    </span>
                    <span className="text-xs" style={{ color: colors.base1 }}>
                      {step.sub}
                    </span>
                  </div>
                  {i < sealingFlow.length - 1 && (
                    <span
                      className="text-2xl font-bold mt-3"
                      style={{ color: colors.base1 }}
                    >
                      →
                    </span>
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {/* How it works */}
          <div
            className="flex-1 rounded-2xl p-6"
            style={{ backgroundColor: colors.base2 }}
          >
            <h3 className="font-semibold text-lg mb-4">
              How verification works
            </h3>
            <div className="space-y-4 text-sm">
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">1. Access window closes</h4>
                <p style={{ color: colors.base1 }}>
                  Edge node bundles logs into tenant-isolated buckets—never
                  shared or aggregated across customers
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">
                  2. ZK proof sealed on-chain
                </h4>
                <p style={{ color: colors.base1 }}>
                  Only the proof hits L1—no sensitive data emitted. Concordium
                  returns a seal with finality.
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.base3 }}
              >
                <h4 className="font-semibold mb-1">3. Audit trail verified</h4>
                <p style={{ color: colors.base1 }}>
                  If local records match the on-chain seal, the audit log is
                  proven untampered
                </p>
              </div>
            </div>
          </div>

          {/* Concordium callout */}
          <div
            className="mt-6 p-4 rounded-xl flex items-center gap-4"
            style={{
              backgroundColor: `${colors.magenta}10`,
              border: `1px solid ${colors.magenta}30`,
            }}
          >
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-xl shrink-0"
              style={{
                backgroundColor: `${colors.magenta}20`,
                color: colors.magenta,
              }}
            >
              ◈
            </div>
            <div>
              <h4 className="font-semibold">Why Concordium?</h4>
              <p className="text-sm" style={{ color: colors.base1 }}>
                From years of chain infra development experience, we wanted
                something no-nonsense. Native ZKPs, stable fees, fast
                finality—infrastructure, not speculation.
              </p>
            </div>
          </div>
        </div>
      </div>

      <SlideFooter
        isDark={isDark}
        left="Idle Grove Edge: Bundle → Seal → Ship"
        right="Grant proposal  •  idlegrove.com"
      />
    </Slide>
  );
}

// =============================================================================
// EXPORT
// =============================================================================
const slides = [BusinessSlide, TechSlide, AuditSlide];

export function BrandSlides() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scale, setScale] = useState(0.5);
  const [isMobile, setIsMobile] = useState(false);

  // Calculate scale to fit viewport
  useEffect(() => {
    const calculateScale = () => {
      const scaleX = (window.innerWidth * 0.95) / 1920;
      const scaleY = (window.innerHeight * 0.95) / 1080;
      setScale(Math.min(scaleX, scaleY, 1)); // Cap at 1x
      setIsMobile(window.innerWidth < 768 || window.innerHeight < 768);
    };
    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);

  const CurrentSlideComponent = slides[currentSlide];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ backgroundColor: "#1a1a1a" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');

        @media print {
          @page {
            size: 508mm 286mm;
            margin: 0;
          }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .slide {
            box-shadow: none !important;
            transform: none !important;
          }
        }

        @media screen {
          .print-only { display: none !important; }
          .slide {
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
        }

        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>

      {/* Visible slide (screen only) */}
      <div className="no-print flex items-center justify-center flex-1 w-full relative">
        {/* Mobile full-screen tap zones with chevrons */}
        {isMobile && (
          <>
            <button
              onClick={goPrev}
              className="absolute top-0 left-0 w-1/2 h-full z-10 flex items-center justify-start pl-4"
              style={{ background: "transparent" }}
              aria-label="Previous slide"
            >
              <span
                className="text-8xl font-thin select-none"
                style={{ color: "rgba(255,255,255,0.08)" }}
              >
                ‹
              </span>
            </button>
            <button
              onClick={goNext}
              className="absolute top-0 right-0 w-1/2 h-full z-10 flex items-center justify-end pr-4"
              style={{ background: "transparent" }}
              aria-label="Next slide"
            >
              <span
                className="text-8xl font-thin select-none"
                style={{ color: "rgba(255,255,255,0.08)" }}
              >
                ›
              </span>
            </button>
            {/* Mobile page counter */}
            <div
              className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium z-20"
              style={{
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "rgba(255,255,255,0.8)",
              }}
            >
              {currentSlide + 1} / {slides.length}
            </div>
          </>
        )}

        <div
          style={{
            width: 1920 * scale,
            height: 1080 * scale,
            position: "relative",
          }}
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: 1920,
              height: 1080,
            }}
          >
            <CurrentSlideComponent />
          </div>
        </div>
      </div>

      {/* Navigation controls - fixed at bottom (desktop only) */}
      {!isMobile && (
      <div className="no-print fixed bottom-6 left-0 right-0 flex flex-col items-center z-50">
        <div
          className="flex items-center gap-6 px-6 py-3 rounded-full"
          style={{
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(8px)",
          }}
        >
          <button
            onClick={goPrev}
            disabled={currentSlide === 0}
            className="px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            {"< "} Prev
          </button>
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className="w-2.5 h-2.5 rounded-full transition-all"
                style={{
                  backgroundColor:
                    i === currentSlide ? colors.cyan : "rgba(255,255,255,0.3)",
                }}
              />
            ))}
          </div>
          <button
            onClick={goNext}
            disabled={currentSlide === slides.length - 1}
            className="px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Next {" >"}
          </button>
          {/* Page counter */}
          <span className="text-white/50 text-sm px-2">
            {currentSlide + 1} / {slides.length}
          </span>
        </div>
        <div className="mt-3 text-white/30 text-xs">
          ← → to navigate · Ctrl+P to print
        </div>
      </div>
      )}

      {/* All slides for print (hidden on screen) */}
      <div className="print-only">
        <BusinessSlide />
        <TechSlide />
        <AuditSlide />
      </div>
    </div>
  );
}
